import React, { useEffect, useState, type FormEvent } from "react";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";
import type { DossierLane, ResearchDossierCaptureV2 } from "@shared/researchDossier";
import ResearchDossierDisplay from "@/components/ResearchDossierDisplay";

type CreateRequest = {
  questionType: "current-quote-valuation-coverage-v1";
  lane: DossierLane;
  symbol: string;
  requestId: string;
};

type DossierResponse = {
  dossier: { id: string; currentReportId: string; questionId: string };
  report: { id: string; title?: string };
  capture: ResearchDossierCaptureV2;
  manifestDigest: string;
  version: { reportId: string; parentReportId: string | null; sequence: number; createdAt: string; status: string };
};

function createUuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  throw new Error("This browser cannot create a secure request ID. Update the browser and try again.");
}

async function readBody(response: Response) {
  try { return await response.json() as Record<string, unknown>; }
  catch { return {}; }
}

function describeFailure(status: number, data: Record<string, unknown>) {
  const detail = typeof data.detail === "string" ? data.detail : typeof data.error === "string" ? data.error : null;
  return status === 0
    ? "No HTTP response was received. The save result is unknown."
    : `Dossier request failed with HTTP ${status}${detail ? `: ${detail}` : "."}`;
}

const ResearchDossierEntry: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [lane, setLane] = useState<DossierLane>(() => {
    if (typeof window === "undefined") return "tradfi";
    return new URLSearchParams(window.location.search).get("lane") === "digital-assets" ? "digital-assets" : "tradfi";
  });
  const [symbol, setSymbol] = useState("");
  const [busy, setBusy] = useState(false);
  const [request, setRequest] = useState<CreateRequest | null>(null);
  const [error, setError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [saved, setSaved] = useState<DossierResponse | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/research-dossiers/config", { signal: controller.signal, cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .then((config) => { if (!controller.signal.aborted) setEnabled(config?.enabled === true); })
      .catch(() => { if (!controller.signal.aborted) setEnabled(false); });
    return () => controller.abort();
  }, []);

  const submitRequest = async (nextRequest: CreateRequest) => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch("/api/research-dossiers", {
        method: "POST",
        credentials: "include",
        headers: { ...getClientIdentityHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(nextRequest),
      });
      const data = await readBody(response);
      if (!response.ok) {
        setError({ message: describeFailure(response.status, data), retryable: response.status >= 500 });
        return;
      }
      if (!data.capture || !data.dossier || !data.report) {
        setError({ message: "The server returned an incomplete save response. The result may have been saved; retry with the same request ID.", retryable: true });
        return;
      }
      setSaved(data as unknown as DossierResponse);
      setRequest(null);
    } catch {
      setError({ message: describeFailure(0, {}), retryable: true });
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!symbol.trim() || busy) return;
    try {
      const nextRequest: CreateRequest = {
        questionType: "current-quote-valuation-coverage-v1",
        lane,
        symbol: symbol.trim(),
        requestId: createUuid(),
      };
      setRequest(nextRequest);
      await submitRequest(nextRequest);
    } catch (failure) {
      setError({ message: failure instanceof Error ? failure.message : "Could not create a request ID.", retryable: false });
    }
  };

  if (!enabled) return null;

  return <section className="analytics-panel-section" aria-labelledby="dossier-entry-title">
    <div className="analytics-command">
      <div>
        <p className="home-kicker">QUESTION-LED DOSSIER · DETERMINISTIC · ZERO MODEL CALLS</p>
        <h2 id="dossier-entry-title">Current quote and valuation coverage</h2>
        <p className="home-summary">As of the server cutoff, which current quote and valuation facts are supported for the selected instrument, what changed from its previous saved assessment, and what remains unavailable? Valuation is reported as unavailable when the admitted evidence does not support it.</p>
      </div>
      <form className="home-symbol-form analytics-form" onSubmit={handleSubmit} aria-label="Create a research dossier">
        <label htmlFor="dossier-lane">Research lane</label>
        <select id="dossier-lane" value={lane} onChange={(event) => setLane(event.target.value as DossierLane)} disabled={busy}>
          <option value="tradfi">TradFi</option>
          <option value="digital-assets">Digital Assets</option>
        </select>
        <label htmlFor="dossier-symbol">Instrument symbol</label>
        <div className="home-symbol-row">
          <input id="dossier-symbol" value={symbol} onChange={(event) => setSymbol(event.target.value)} autoComplete="off" maxLength={32} disabled={busy} placeholder="Enter a supported symbol" required />
          <button type="submit" disabled={busy || !symbol.trim() || (!!request && error?.retryable === true)}>{busy ? "Saving…" : "Create saved assessment"}</button>
        </div>
      </form>
      {error && <div className="analytics-error" role="alert" aria-live="assertive">
        <p>{error.message}</p>
        {error.retryable && request && <button type="button" className="analytics-action-btn" onClick={() => void submitRequest(request)} disabled={busy}>Retry the same request</button>}
      </div>}
      {saved && <div aria-live="polite">
        <p>Saved assessment version {saved.version.sequence}. No model call was made.</p>
        <a className="analytics-action-btn" href={`/reports?id=${encodeURIComponent(saved.report.id)}`}>Open saved dossier in Reports</a>
        <ResearchDossierDisplay capture={saved.capture} versions={[saved.version]} />
      </div>}
    </div>
  </section>;
};

export default ResearchDossierEntry;
